import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/layout/Layout";
import { Board } from "@/components/board/Board";
import { GameHUD } from "@/components/game/GameHUD";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getEngine } from "@/game/registry";
import { getRuleset } from "@/game/rulesets";
import { useSocket } from "@/hooks/useSocket";
import { useAuth, API, authedHeaders } from "@/hooks/useAuth";
import { Send, Flag, Home } from "lucide-react";
import { toast } from "sonner";

const PLAYER_A = "A";
const PLAYER_B = "B";

export function OnlineGame() {
  const navigate = useNavigate();
  const { gameId } = useParams();
  const [params] = useSearchParams();
  const rulesetId = params.get("ruleset") || "zimbabwe-standard";
  const youAre = params.get("side") === "B" ? PLAYER_B : PLAYER_A;
  const opponentId = params.get("opp");
  const opponentName = params.get("opp_name") || "Opponent";
  const { user } = useAuth();
  const { ready, send, subscribe } = useSocket();

  const ruleset = getRuleset(rulesetId);
  const engine = useMemo(() => getEngine(rulesetId), [rulesetId]);
  const [state, setState] = useState(() => engine.createInitialState(rulesetId, { sowingDirection: "CCW" }));
  const startedAtRef = useRef(Date.now());
  const settledRef = useRef(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const legalMoves = useMemo(() => engine.getLegalMoves(state), [engine, state]);
  const legalSet = useMemo(
    () => new Set(legalMoves.map(([r, c]) => `${r},${c}`)),
    [legalMoves]
  );
  const myTurn = state.currentPlayer === youAre && state.status === "ACTIVE";

  const reportResult = useCallback(
    (outcome) => {
      if (settledRef.current || !user) return;
      settledRef.current = true;
      axios
        .post(
          `${API}/games/pvp-result`,
          {
            game_id: gameId,
            ruleset_id: rulesetId,
            opponent_id: opponentId,
            outcome,
            moves: state.moveNumber,
            captures: (state.captures?.A || 0) + (state.captures?.B || 0),
            duration_ms: Date.now() - startedAtRef.current,
          },
          { headers: authedHeaders() }
        )
        .then((res) => {
          if (res.data?.rating?.delta != null) {
            const delta = Math.round(res.data.rating.delta);
            const sign = delta >= 0 ? "+" : "";
            toast.message(`Rating: ${Math.round(res.data.rating.rating)} (${sign}${delta})`);
          }
        })
        .catch(() => {});
    },
    [gameId, opponentId, rulesetId, state.moveNumber, state.captures, user]
  );

  // Apply a move locally and emit/synchronise. `remote` skips emitting.
  const applyLocalMove = useCallback(
    (r, c, remote = false) => {
      try {
        const { state: next } = engine.applyMove(state, r, c);
        setState(next);
        if (!remote) {
          send({ type: "GAME_MOVE", game_id: gameId, move: { r, c } });
        }
        if (next.status !== "ACTIVE") {
          if (next.status === "WON") {
            const outcome = next.winner === youAre ? "win" : "loss";
            reportResult(outcome);
            send({ type: "GAME_END", game_id: gameId });
          } else if (next.status === "DRAWN") {
            reportResult("draw");
            send({ type: "GAME_END", game_id: gameId });
          }
        }
      } catch (e) {
        if (!remote) toast.error("Illegal move.");
        else toast.error("Out-of-sync move from opponent.");
      }
    },
    [engine, state, send, gameId, youAre, reportResult]
  );

  // Listen for opponent moves / chat / end.
  useEffect(() => {
    const unsub = subscribe((s) => {
      const m = s.message;
      if (!m) return;
      if (m.type === "GAME_MOVE" && m.game_id === gameId) {
        applyLocalMove(m.move.r, m.move.c, true);
      } else if (m.type === "CHAT" && m.room === gameId) {
        setMessages((prev) => [...prev.slice(-49), m]);
      } else if (m.type === "GAME_END" && m.game_id === gameId) {
        if (m.reason === "RESIGN" && m.winner !== user?.id) {
          // Our opponent resigned us into a win.
          setStatusMessage("Opponent resigned. You win!");
          reportResult("win");
        }
      }
    });
    return unsub;
  }, [subscribe, gameId, applyLocalMove, user?.id, reportResult]);

  // Join the game room for chat once connected.
  useEffect(() => {
    if (!ready) return;
    // The room is auto-joined server-side when GAME_START fires, but if the
    // user navigated directly we send a no-op chat to ensure presence.
  }, [ready]);

  const onPitSelect = (r, c) => {
    if (!myTurn) return;
    if (!legalSet.has(`${r},${c}`)) return;
    applyLocalMove(r, c);
  };

  const sendChat = (e) => {
    e?.preventDefault?.();
    if (!draft.trim()) return;
    if (send({ type: "CHAT", room: gameId, text: draft.trim() })) setDraft("");
  };

  const resign = () => {
    if (state.status !== "ACTIVE") return;
    send({ type: "GAME_RESIGN", game_id: gameId });
    setStatusMessage("You resigned. Returning to lobby…");
    reportResult("loss");
    setTimeout(() => navigate("/lobby"), 2000);
  };

  return (
    <Layout hideFooter>
      <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Online · {ruleset?.name}</p>
              <h1 className="font-display text-2xl mt-1">
                You vs <span className="text-primary">{opponentName}</span>
              </h1>
            </div>
            <Badge variant={myTurn ? "default" : "outline"} data-testid="turn-indicator" className="rounded-full">
              {state.status !== "ACTIVE"
                ? "Game over"
                : myTurn ? "Your turn" : "Waiting for opponent…"}
            </Badge>
          </div>

          {statusMessage && (
            <Card data-testid="status-banner" className="p-3 border-primary/50 text-sm">{statusMessage}</Card>
          )}

          <GameHUD state={state} rulesetName={ruleset?.name || "Tsoro"} mode="pvp" humanSide={youAre} />

          <Board
            board={state.board}
            currentPlayer={state.currentPlayer}
            legalSet={myTurn ? legalSet : new Set()}
            disabled={!myTurn || state.status !== "ACTIVE"}
            showHints
            sowingDirection="CCW"
            showDirectionBanner={rulesetId === "zimbabwe-standard"}
            onPitSelect={onPitSelect}
          />

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={resign} disabled={state.status !== "ACTIVE"} data-testid="resign-btn" className="rounded-full">
              <Flag className="h-4 w-4 mr-2" /> Resign
            </Button>
            <Button variant="ghost" onClick={() => navigate("/lobby")} className="rounded-full">
              <Home className="h-4 w-4 mr-2" /> Back to lobby
            </Button>
          </div>
        </div>

        <aside className="lg:col-span-4 flex flex-col">
          <Card className="p-4 flex-1 flex flex-col min-h-[400px]">
            <h2 className="font-display text-xl mb-2">Game chat</h2>
            <div data-testid="game-chat" className="flex-1 min-h-[260px] max-h-[480px] overflow-y-auto bg-muted/30 rounded-lg p-3 space-y-1.5">
              {messages.length === 0 ? (
                <p className="text-sm text-muted-foreground">Say hello. Be kind — your opponent can hear you.</p>
              ) : (
                messages.map((m, i) => (
                  <p key={i} className="text-sm">
                    <strong className={m.from === user?.id ? "text-foreground" : "text-primary"}>
                      {m.from === user?.id ? "you" : m.username}:
                    </strong>{" "}
                    <span className="text-foreground/90">{m.text}</span>
                  </p>
                ))
              )}
            </div>
            <form onSubmit={sendChat} className="flex gap-2 mt-3">
              <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type…" data-testid="game-chat-input" maxLength={500} />
              <Button type="submit" data-testid="game-chat-send" className="rounded-full">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </Card>
        </aside>
      </section>
    </Layout>
  );
}
